import { Link } from 'react-router-dom';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import logo from '../../../assets/images/logo.svg';
import searchIcon from '../../../assets/images/search-icon.svg';
import closeIcon from '../../../assets/images/close-icon.svg';
import { CORDER_SIDE_BAR } from '../../../static/ApiData/corder-api.constants';

const SideBar = ({ onSelectApiType, onScrollToTop, hash }) => {
	const { t } = useTranslation();
	const [dropDown, setDropDown] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const sidebarRef = useRef(null);
	const skipAutoScrollRef = useRef(false);

	const selectedSidebar = CORDER_SIDE_BAR;

	const getAllTypes = () => {
		const allTypes = [];
		selectedSidebar.forEach((sidebar) => {
			sidebar.apiFolder.forEach((folder) => {
				folder.types?.forEach((type) => {
					allTypes.push({
						...type,
						folderId: folder.id,
						folderName: folder.name,
						sidebarPortal: sidebar.portal,
					});
				});
			});
		});
		return allTypes;
	};

	const handleSearch = (e) => {
		const term = e.target.value.toLowerCase();
		setSearchTerm(term);

		if (term.length < 2) {
			setSearchResults([]);
			setIsSearching(false);
			return;
		}

		setIsSearching(true);

		const results = getAllTypes().filter(
			(type) =>
				t(type.name).toLowerCase().includes(term) || type.id.toLowerCase().includes(term)
		);

		setSearchResults(results);
	};

	const clearSearch = () => {
		setSearchTerm('');
		setSearchResults([]);
		setIsSearching(false);
	};

	const handleResultClick = (typeId, folderId) => {
		setDropDown(folderId);
		if (onSelectApiType) onSelectApiType(typeId);
		clearSearch();
	};

	const handleClick = (apiTypeId) => {
		skipAutoScrollRef.current = true;
		if (onSelectApiType) onSelectApiType(apiTypeId);
	};

	const handleGroupClick = (folder) => {
		skipAutoScrollRef.current = true;
		setDropDown((prev) => (prev === folder.id ? 0 : folder.id));
		if (onSelectApiType) onSelectApiType(folder.id);
	};

	const handlePortalClick = () => {
		skipAutoScrollRef.current = true;
		if (onScrollToTop) onScrollToTop();
	};

	useEffect(() => {
		if (!hash) return;

		let matchedFolderId = null;

		selectedSidebar.forEach((sidebar) => {
			sidebar.apiFolder.forEach((folder) => {
				if (folder.id === hash || folder.types?.some((type) => type.id === hash)) {
					matchedFolderId = folder.id;
				}
			});
		});

		if (matchedFolderId) {
			setDropDown(matchedFolderId);
		}
	}, [hash]);

	useLayoutEffect(() => {
		if (!hash) return;

		if (skipAutoScrollRef.current) {
			skipAutoScrollRef.current = false;
			return;
		}

		let targetId = hash;

		selectedSidebar.forEach((sidebar) => {
			sidebar.apiFolder.forEach((folder) => {
				if (folder.types?.some((type) => type.id === hash)) {
					targetId = folder.id;
				}
			});
		});

		const activeItem = sidebarRef.current?.querySelector(`[data-sidebar-id="${targetId}"]`);
		if (!activeItem || !sidebarRef.current) return;

		sidebarRef.current.scrollTo({
			top: activeItem.offsetTop - 20,
			behavior: 'smooth',
		});
	}, [hash, dropDown]);

	return (
		<div
			ref={sidebarRef}
			className='sticky top-0 h-screen overflow-y-auto border-r border-gray-200 bg-white shadow-sm'
		>
			<div className='flex h-full flex-col'>
				<Link
					to='/'
					className='hidden items-center gap-3 border-b border-gray-200 px-5 py-4 no-underline md:flex'
				>
					<div className='flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-50'>
						<img alt='header' src={logo} className='m-0 w-7' />
					</div>
					<div className='text-lg font-extrabold text-gray-900'>
						Mereos
					</div>
				</Link>

				<div className='px-4 py-4 md:px-5'>
					<div className='relative'>
						<img
							alt='searchIcon'
							src={searchIcon}
							className='pointer-events-none absolute left-3 top-1/2 m-0 w-4 -translate-y-1/2 opacity-60'
						/>
						<input
							type='text'
							placeholder='Search endpoint'
							className='api-doc-sidebar-search w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-9 text-xs text-gray-700 outline-none transition focus:border-[#fdc137] focus:bg-white focus:ring-2 focus:ring-[#fff8e1]'
							value={searchTerm}
							onChange={handleSearch}
						/>
						{searchTerm && (
							<button
								onClick={clearSearch}
								className='absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition hover:bg-gray-200'
							>
								<img src={closeIcon} alt='clear' className='m-0 h-3 w-3' />
							</button>
						)}
					</div>

					{isSearching && (
						<div className='api-doc-search-dropdown mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md'>
							{searchResults.length > 0 ? (
								<div className='max-h-60 overflow-y-auto py-0.5'>
									<div className='api-doc-search-meta text-xs font-medium text-gray-500'>
										Found {searchResults.length} result(s)
									</div>

									{searchResults.map((result, index) => (
										<button
											key={index}
											onClick={() => handleResultClick(result.id, result.folderId)}
											className='flex w-full flex-col rounded-md px-2 py-1.5 text-left transition hover:bg-[#fff8e1]'
										>
											<span className='text-sm font-semibold text-gray-800'>
												{t(result.name)}
											</span>
											<span className='mt-0.5 text-xs text-gray-500'>
												{t(result.folderName)} • {t(result.sidebarPortal)}
											</span>
										</button>
									))}
								</div>
							) : (
								<div className='px-2 py-2 text-center text-sm text-gray-500'>
									No results found for &quot;{searchTerm}&quot;
								</div>
							)}
						</div>
					)}
				</div>

				<div className='flex-1 px-3 pb-5 md:px-4'>
					{selectedSidebar?.map((sidebar, index) => (
						<div key={index} className='mb-6'>
							<button
								onClick={handlePortalClick}
								className='mb-3 w-full rounded-xl px-3 py-2 text-left text-sm font-semibold uppercase tracking-wide text-gray-500 transition hover:bg-[#fff8e1] hover:text-amber-800'
							>
								{t(sidebar.portal)}
							</button>

							<div className='space-y-2'>
								{sidebar.apiFolder.slice().map((folder, apiIndex) => {
									const isOpen = dropDown === folder.id;

									return (
										<div
											key={apiIndex}
											className='overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/70'
										>
											<button
												data-sidebar-id={folder.id}
												onClick={() => handleGroupClick(folder)}
												className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium transition ${isOpen
														? 'bg-[#fff8e1] text-amber-900'
														: 'text-gray-700 hover:bg-[#fff8e1]'
													}`}
											>
												<span className={folder.bold ? 'font-semibold' : ''}>
													{t(folder.name)}
												</span>

												<span
													className={`text-lg leading-none transition-transform ${isOpen ? 'rotate-90 text-[#fdc137]' : 'text-gray-400'
														}`}
												>
													›
												</span>
											</button>

											{isOpen && (
												<div className='border-t border-gray-200 bg-white px-2 py-2'>
													<div className='space-y-1'>
														{folder?.types?.map((file, folderIndex) => {
															const isActive = hash === file.id;

															return (
																<button
																	key={folderIndex}
																	data-sidebar-id={file.id}
																	onClick={() => handleClick(file.id)}
																	className={`flex w-full items-start rounded-xl px-3 py-2 text-left text-sm transition ${isActive
																			? 'bg-[#fdc137] text-gray-900 font-medium shadow-sm'
																			: 'text-gray-600 hover:bg-[#fff8e1] hover:text-gray-900'
																		}`}
																>
																	<span className='break-words whitespace-normal'>
																		{t(file.name)}
																	</span>
																</button>
															);
														})}
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default SideBar;
